const GTM_EVENT_TYPE = 'cta_click' as const;
const GTM_ACTION = 'click' as const;

type GtmEventType = typeof GTM_EVENT_TYPE;
type GtmAction = typeof GTM_ACTION;
type GtmCategory = 'button' | 'link' | 'navigation' | 'image_editor' | 'sample_picker' | 'blind_control';
type GtmLocation =
	| 'footer'
	| 'toolbar'
	| 'overview_page'
	| 'control_panel'
	| 'view_navigation'
	| 'header'
	| 'image-cropper'
	| 'sample-project-selection';

type GtmEvent = {
	event: GtmEventType;
	category: GtmCategory;
	action: GtmAction;
	label: string;
	location?: GtmLocation;
};

const createEvent = (label: string, category: GtmCategory, location?: GtmLocation): GtmEvent => ({
	event: GTM_EVENT_TYPE,
	category,
	action: GTM_ACTION,
	label,
	location,
});

const defineEvents = <T extends Record<string, GtmEvent | ((code: string) => GtmEvent)>>(events: T): T => {
	return events;
};

export const GTM_HEADER_EVENTS = defineEvents({
	LOGO: createEvent('Logo click', 'navigation', 'header'),
	DESIGN_LINK: createEvent('Design page', 'navigation', 'header'),
	OVERVIEW_LINK: createEvent('Overview page', 'navigation', 'header'),
	COUNTRY_SELECTOR: createEvent('Country selector', 'navigation', 'header'),
	CONTROL_PANEL: createEvent('Control Panel', 'navigation', 'header'),
	CONTROL_PANEL_VISIBLE: createEvent('Control Panel Visible', 'button', 'header'),
	CONTROL_PANEL_HELP: createEvent('Control Panel Help', 'button', 'header'),
	EXIT_APP: createEvent('Exit app', 'button', 'header'),
});

export const GTM_COUNTRY_EVENS = defineEvents({
	CHANGE_COUNTRY: (code: string) => createEvent(`Changed Country to ${code}`, 'navigation', 'header'),
});

export const GTM_EVENTS = defineEvents({
	OVERVIEW_BUTTON: createEvent('See Overview', 'button'),
	OUTDOOR: createEvent('Outdoor blinds', 'button'),
	INTERIOR: createEvent('Interior blinds', 'button'),
	UPLOAD: createEvent('Use my own photo', 'button'),
	PROJECT_SAMPLE: createEvent('Use a sample project', 'button'),
	UPLOAD_PHOTO_BUTTON: createEvent('Upload Photo', 'button'),
	USE_PHOTO_BUTTON: createEvent('Use this Photo', 'button'),
	USE_PROJECT_BUTTON: createEvent('Use this Project', 'button'),
	FEEDBACK_MODAL: createEvent('Leave us Feedback', 'button', 'footer'),
	QUIZ_MODAL: createEvent('Leave us Feedback', 'button', 'footer'),
	COLLAPSE_TOOLBAR: createEvent('Collapse Toolbar arrow', 'button', 'toolbar'),
	LIVE_BACKGROUND: createEvent('Live background view', 'button'),
	AR: createEvent('AR view', 'button'),
});

export const GTM_OVERVIEW_EVENTS = defineEvents({
	EDIT_BLIND: createEvent('Edit blind overview page', 'button', 'overview_page'),
	IV_BLIND: createEvent('IV blind overview page', 'button', 'overview_page'),
	DELETE_BLIND: createEvent('Delete blind overview page', 'button', 'overview_page'),
	DOWNLOAD_PDF: createEvent('Download PDF', 'button', 'overview_page'),
	SAVE_WORK: createEvent('Save your work and email link', 'button', 'overview_page'),
	SHOW_MAP: createEvent('Show on a Map', 'button', 'overview_page'),
	BACK_TO_TOP: createEvent('Back to Top button', 'button', 'overview_page'),
	PRIVACY_LINK: createEvent('If you would like to see more regarding privacy link', 'link', 'overview_page'),
});

export const GTM_CONTROL_EVENTS = defineEvents({
	RESET: createEvent('Reset All Blinds', 'button', 'control_panel'),
	BACKGROUND: createEvent('Select Background', 'button', 'control_panel'),
	SAVE: createEvent('Save session / image', 'button', 'control_panel'),
	DOWNLOAD_IMAGE: createEvent('Download Image', 'button', 'control_panel'),
	SAVE_WORK: createEvent('Save Work control button', 'button', 'control_panel'),
});

export const GTM_VIEW_EVENTS = defineEvents({
	IV: createEvent('Try in your space', 'navigation', 'view_navigation'),
	DYB: createEvent('Interactive 3D', 'navigation', 'view_navigation'),
	DAY: createEvent('Day view', 'navigation', 'view_navigation'),
	NIGHT: createEvent('Night view', 'navigation', 'view_navigation'),
	SHUTTER_CONTROL: createEvent('Shutter Control', 'blind_control', 'view_navigation'),
	SHUTTER_CHANGE: createEvent('Switch Control Change', 'blind_control', 'view_navigation'),
});

export const GTM_CROPPER_EVENTS = defineEvents({
	FLIP: createEvent('Image Cropper flip horizontal', 'image_editor', 'image-cropper'),
	ROTATE: createEvent('Image Cropper rotate', 'image_editor', 'image-cropper'),
	ZOOM_IN: createEvent('Image Cropper Zoom in', 'image_editor', 'image-cropper'),
	ZOOM_OUT: createEvent('Image Cropper Zoom out', 'image_editor', 'image-cropper'),
	RESET: createEvent('Image Cropper Reset', 'image_editor', 'image-cropper'),
	DELETE: createEvent('Image Cropper Delete', 'image_editor', 'image-cropper'),
	UPLOAD: createEvent('Image Cropper Upload', 'image_editor', 'image-cropper'),
});

export const GTM_SAMPLE_EVENTS: Record<'outdoor' | 'interior', GtmEvent[]> = {
	outdoor: [
		createEvent('Outdoor sample photo 1', 'sample_picker', 'sample-project-selection'),
		createEvent('Outdoor sample photo 2', 'sample_picker', 'sample-project-selection'),
		createEvent('Outdoor sample photo 3', 'sample_picker', 'sample-project-selection'),
	],
	interior: [
		createEvent('Interior sample photo 1', 'sample_picker', 'sample-project-selection'),
		createEvent('Interior sample photo 2', 'sample_picker', 'sample-project-selection'),
		createEvent('Interior sample photo 3', 'sample_picker', 'sample-project-selection'),
	],
};
